import { Container, Graphics, Sprite, type Texture } from 'pixi.js';
import { glowTexture } from '../road/glow';
import { AudioBus } from '../../audio/AudioBus';
import { STORY_SOUNDS } from '../../logic/story/sounds';
import type { SceneLayoutContext } from '../../scenes/SceneComponent';
import { animate, Confetti, ease } from '../board/index';
import { display, fitSprite } from './text';

export interface GiftPrize {
  coins: number;
  gems: number;
  freeSpins: number;
}

/**
 * The mystery box: it drops onto the reels and waits to be tapped (the reels
 * dim behind it, so it can't be missed or spun past). Opening asks the server
 * what's inside; the box bursts and shows the prize, then the host flies it
 * home.
 */
export class GiftBox extends Container {
  private readonly dim = new Graphics();
  private readonly glow = new Sprite();
  private readonly box = new Sprite();
  private readonly hint = display('TAP TO OPEN', 22);
  private readonly prize = new Container();
  private readonly confetti = new Confetti();
  private ctx: SceneLayoutContext | null = null;
  private pulse: ReturnType<typeof setInterval> | null = null;

  constructor(
    boxTexture: Texture | null,
    private readonly art: (name: 'coin' | 'gem' | 'spin') => Texture | null,
  ) {
    super();
    if (boxTexture) this.box.texture = boxTexture;
    this.box.anchor.set(0.5);
    this.box.label = 'gift-box';
    this.hint.anchor.set(0.5);
    this.dim.eventMode = 'static';
    this.box.eventMode = 'static';
    this.box.cursor = 'pointer';
    this.addChild(this.dim, this.glow, this.box, this.hint, this.prize, this.confetti);
    this.visible = false;
  }

  layout(ctx: SceneLayoutContext): void {
    this.ctx = ctx;
    this.dim.clear().rect(0, 0, ctx.width, ctx.height).fill({ color: 0x0a0602, alpha: 0.55 });
  }

  private centre(): { x: number; y: number; size: number } {
    const c = this.ctx!;
    const left = c.machineLeft ?? 0;
    const right = c.machineRight ?? c.width;
    const size = Math.round(Math.min(170, Math.max(110, (right - left) * 0.38)));
    return { x: (left + right) / 2, y: (c.machineTop + c.machineBottom) / 2 - 10, size };
  }

  /**
   * Drop the box, wait for a tap, open it with `open` (the server), show what
   * was inside, and hand it to `deliver` (coins fly to the wallet). Resolves
   * when the box has gone.
   */
  async play(open: () => Promise<GiftPrize>, deliver: (prize: GiftPrize, at: { x: number; y: number }) => Promise<void>): Promise<void> {
    if (!this.ctx) return;
    const { x, y, size } = this.centre();
    this.prize.removeChildren();
    this.visible = true;
    this.alpha = 1;
    this.box.alpha = 1;
    this.box.scale.set(size / Math.max(1, this.box.texture.width, this.box.texture.height));
    const base = this.box.scale.x;
    this.hint.position.set(x, y + size * 0.62);
    this.hint.alpha = 0;
    this.glow.texture = glowTexture();
    this.glow.anchor.set(0.5);
    this.glow.tint = 0xffd36a;
    this.glow.width = this.glow.height = size * 1.9;
    this.glow.position.set(x, y);
    AudioBus.shared.play(STORY_SOUNDS.boxDrop, { volume: 0.8 });
    await animate(520, (t) => {
      const e = ease.outBack(t);
      this.box.position.set(x, y - (1 - e) * (y + size));
      this.glow.alpha = t;
    });
    this.startPulse(base);
    for (let tries = 0; ; tries++) {
      this.hint.text = tries === 0 ? 'TAP TO OPEN' : 'TAP TO TRY AGAIN';
      this.hint.alpha = 1;
      await new Promise<void>((resolve) => this.box.once('pointertap', () => resolve()));
      this.hint.text = 'OPENING';
      try {
        const prize = await open();
        this.stopPulse(base);
        await this.burst(prize, x, y, size, base);
        // Every prize in the box is a win: confetti as it shows.
        void this.confetti.burst(x, y, { count: 90, spread: Math.max(260, size * 2) });
        await deliver(prize, { x, y });
        break;
      } catch {
        if (tries < 1) continue;
        // No connection: the server opens it at the next spin, so nothing
        // is lost and the reels aren't held up.
        this.stopPulse(base);
        this.hint.text = 'IT WILL OPEN ON YOUR NEXT SPIN';
        if (this.ctx) this.hint.scale.set(Math.min(1, (this.ctx.width - 24) / (this.hint.width / this.hint.scale.x)));
        await new Promise((r) => setTimeout(r, 1800));
        break;
      }
    }
    this.hint.scale.set(1);
    await new Promise((r) => setTimeout(r, 700));
    await animate(260, (t) => (this.alpha = 1 - t));
    this.visible = false;
  }

  private startPulse(base: number): void {
    let t = 0;
    this.pulse = setInterval(() => {
      t += 0.12;
      this.box.scale.set(base * (1 + Math.sin(t) * 0.04));
      this.box.rotation = Math.sin(t * 0.7) * 0.04;
      this.hint.alpha = 0.75 + Math.sin(t) * 0.25;
    }, 33);
  }

  private stopPulse(base: number): void {
    if (this.pulse) clearInterval(this.pulse);
    this.pulse = null;
    this.box.scale.set(base);
    this.box.rotation = 0;
  }

  private async burst(prize: GiftPrize, x: number, y: number, size: number, base: number): Promise<void> {
    AudioBus.shared.play(STORY_SOUNDS.boxOpen, { volume: 0.85 });
    this.hint.alpha = 0;
    const [icon, text] =
      prize.coins > 0
        ? (['coin', `+${prize.coins.toLocaleString('en-GB')}`] as const)
        : prize.freeSpins > 0
          ? (['spin', `${prize.freeSpins} FREE SPINS`] as const)
          : (['gem', `+${prize.gems} GEMS`] as const);
    const label = display(text, 44);
    label.anchor.set(0, 0.5);
    const pic = fitSprite(this.art(icon), 54);
    pic.anchor.set(0.5);
    const w = 54 + 10 + label.width;
    pic.position.set(-w / 2 + 27, 0);
    label.position.set(-w / 2 + 64, 0);
    this.prize.addChild(pic, label);
    this.prize.label = 'gift-prize';
    this.prize.position.set(x, y);
    this.prize.alpha = 0;
    const fit = Math.min(1, ((this.ctx?.width ?? w + 32) - 32) / w);
    await animate(420, (raw) => {
      const t = ease.outBack(raw);
      this.box.scale.set(base * (1 + 0.5 * raw));
      this.box.alpha = 1 - raw;
      this.glow.width = this.glow.height = size * (1.9 + raw * 1.4);
      this.prize.alpha = raw;
      this.prize.scale.set(fit * (0.5 + 0.5 * t));
    });
  }
}
